import { action, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  addGenericErrorForProperty,
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { manageEach, moveItem } from '@/features/legends/editor/collections'
import { validateElements } from '@/utils/validx-util'
import type { EffectEditorViewModel } from './EffectEditorViewModel'
import type { KnownMutator, Mutator, MutatorType } from './effects'
import {
  type AnyMutatorEditor,
  createMutator,
  hydrateMutator,
} from './mutator-registry'

export interface MutatorsBuilderViewModelOptions {
  effect: EffectEditorViewModel
  dependencies: LegendPartDependencies
}

/**
 * The mutators of an effect, in the order they run.
 */
export class MutatorsBuilderViewModel extends LegendPartViewModelBase {
  private readonly _effect: EffectEditorViewModel
  private readonly _schema: IValidationSchema<MutatorsBuilderViewModel> = {
    mutators: [validateElements()],
  }

  readonly mutators = observable.array<AnyMutatorEditor>([], { deep: false })

  readonly validation = validationContext(this, this._schema)

  constructor({ effect, dependencies }: MutatorsBuilderViewModelOptions) {
    super(dependencies)
    this._effect = effect
    this.lifecycle.manage(manageEach(this.lifecycle, () => this.mutators))
  }

  /**
   * Sets the mutators from the legend document.
   */
  @action
  hydrate(mutators: Mutator[] | undefined) {
    this.mutators.replace(
      (mutators ?? []).map((m) => hydrateMutator(m, this._mutatorOptions)),
    )
  }

  /**
   * Adds an empty mutator of the type and focuses it.
   */
  @action.bound
  add(type: MutatorType) {
    const mutator = createMutator(type, this._mutatorOptions)
    this.mutators.push(mutator)
    mutator.focus()
  }

  /**
   * Removes the mutator.
   */
  @action.bound
  remove(mutator: AnyMutatorEditor) {
    this.mutators.remove(mutator)
  }

  /**
   * Moves a mutator, changing when it runs.
   */
  @action.bound
  moveByIndex(fromIndex: number, toIndex: number) {
    moveItem(this.mutators, fromIndex, toIndex)
  }

  /**
   * Shows the analyzer's errors on the mutators.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    addGenericErrorForProperty(
      this,
      descriptor,
      'mutators',
      'There are invalid mutators.',
    )
    receiveErrors(this.mutators, descriptor.inner)
  }

  /**
   * The mutators as the legend document stores them.
   */
  toJSON(): KnownMutator[] {
    return this.mutators.map((m) => m.toJSON())
  }

  private get _mutatorOptions() {
    return { effect: this._effect, dependencies: this.dependencies }
  }
}
